"use client";

import {
  useActionState, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type RefObject,
} from "react";
import {
  submitFeedback, listMyFeedback, type FeedbackState, type MyFeedbackItem,
} from "@/app/actions/feedback";
import { STATUS_LABELS, type FeedbackStatus } from "@/lib/feedback-validation";
import { FeedbackThread } from "./FeedbackThread";
import { formatDateTime } from "@/lib/format";
import { SelectMenu } from "./SelectMenu";
import { StatusDot } from "./StatusDot";
import { useDialog } from "./useDialog";
import DraftNotice from "@/components/DraftNotice";
import { useFormDraft, formPort } from "@/components/useFormDraft";

const INITIAL: FeedbackState = { ok: false };

/** The type dropdown publishes through a hidden input that MIRRORS SelectMenu's
 *  React state. Restoring that mirror would move the submitted value without
 *  moving the visible label, and the next render races the old value back in —
 *  the form would then disagree with itself about what it is about to send. The
 *  message is the part worth keeping; the type is one tap to re-choose.
 *
 *  `pageUrl`/`userAgent` need no entry here: this widget sets them on the
 *  FormData at submit time rather than carrying them as hidden inputs, so they
 *  are never in the form to be read. Nothing else is listed — a name in
 *  `exclude` that matches no field today is a silent drop the day it gains
 *  one. */
const DRAFT_EXCLUDE = ["type"];
// No emoji: `globals.css` states the direction's marks "are painted or they
// are Alfa Slab lettering, never a unicode glyph". `.feedback-item-type`
// already renders the type in Alfa Slab uppercase, so the label text alone
// carries it.
const TYPE_OPTIONS = [
  { value: "bug", label: "Bug" },
  { value: "idea", label: "Idea" },
  { value: "other", label: "Other" },
];

const TAB_ORDER = ["send", "mine"] as const;
type FeedbackTab = (typeof TAB_ORDER)[number];

/** A painted disclosure mark, an inline SVG — never a unicode glyph (`globals.css`'s rule). */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`feedback-item-chevron${open ? " is-open" : ""}`}
      viewBox="0 0 12 8"
      aria-hidden="true"
    >
      <path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  // Bumped each open so the inner panel (and its action state) remounts fresh,
  // letting a user submit more than once per session.
  const [instance, setInstance] = useState(0);

  // The draft itself lives down in FeedbackForm, beside the form ref and the
  // action state it has to recover from — it MUST NOT be hoisted here, because
  // this widget outlives the form. A hook up here mounts while the form does
  // not exist, captures an empty baseline off a null ref, and later deletes the
  // message when the "My submissions" tab unmounts the form. But every way of
  // CLOSING this modal is up here or between the two (the backdrop, Escape, the
  // ×, Cancel) and they all funnel through `closeModal`. So the form publishes
  // its `clear` into this ref and closing calls it, rather than four close paths
  // each learning about drafts. Safe only because the scope is CONSTANT.
  const clearDraft = useRef<(() => void) | null>(null);

  const closeModal = useCallback(() => {
    // Closing the modal IS a deliberate discard — the message was written in a
    // sheet the user then dismissed — so the draft goes with it. A tab that
    // dies with the modal still OPEN keeps the message, which is the case this
    // feature exists for. Switching to "My submissions" and back does not close
    // the modal, so that keeps it too.
    clearDraft.current?.();
    setOpen(false);
  }, []);

  function openModal() {
    setInstance((n) => n + 1);
    setOpen(true);
  }

  return (
    <>
      <button className="feedback-fab" onClick={openModal} aria-label="Send feedback">Feedback</button>
      {open && (
        <div className="feedback-backdrop" onClick={closeModal}>
          <FeedbackDialog instance={instance} onClose={closeModal} clearDraft={clearDraft} />
        </div>
      )}
    </>
  );
}

function FeedbackDialog({
  instance,
  onClose,
  clearDraft,
}: {
  instance: number;
  onClose: () => void;
  /** Filled in by FeedbackForm, called by FeedbackWidget's `closeModal`. */
  clearDraft: RefObject<(() => void) | null>;
}) {
  // Escape, focus-in, focus-return and the Tab trap.
  const dialog = useDialog<HTMLDivElement>(onClose);
  return (
    <div className="feedback-modal card" role="dialog" aria-modal="true" aria-label="Feedback"
      onClick={(e) => e.stopPropagation()} {...dialog}>
      <FeedbackPanel key={instance} onClose={onClose} clearDraft={clearDraft} />
    </div>
  );
}

function FeedbackPanel({
  onClose,
  clearDraft,
}: {
  onClose: () => void;
  clearDraft: RefObject<(() => void) | null>;
}) {
  const [tab, setTab] = useState<FeedbackTab>("send");
  const sendTabId = useId();
  const mineTabId = useId();
  const sendPanelId = useId();
  const minePanelId = useId();
  const sendTabRef = useRef<HTMLButtonElement>(null);
  const mineTabRef = useRef<HTMLButtonElement>(null);

  // Roving tabindex + arrow-key movement, per the ARIA tabs pattern the two
  // `role="tab"` buttons below already promise but did not, until now, keep.
  function onTabKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    const i = TAB_ORDER.indexOf(tab);
    let next: FeedbackTab | null = null;
    if (e.key === "ArrowRight") next = TAB_ORDER[(i + 1) % TAB_ORDER.length];
    else if (e.key === "ArrowLeft") next = TAB_ORDER[(i - 1 + TAB_ORDER.length) % TAB_ORDER.length];
    else if (e.key === "Home") next = TAB_ORDER[0];
    else if (e.key === "End") next = TAB_ORDER[TAB_ORDER.length - 1];
    if (!next) return;
    e.preventDefault();
    setTab(next);
    (next === "send" ? sendTabRef : mineTabRef).current?.focus();
  }

  return (
    <>
      <div className="feedback-head">
        <div className="feedback-tabs" role="tablist" aria-label="Feedback">
          <button
            ref={sendTabRef}
            role="tab"
            id={sendTabId}
            aria-selected={tab === "send"}
            aria-controls={sendPanelId}
            tabIndex={tab === "send" ? 0 : -1}
            className={`feedback-tab${tab === "send" ? " is-active" : ""}`}
            onClick={() => setTab("send")}
            onKeyDown={onTabKeyDown}
          >
            Send feedback
          </button>
          <button
            ref={mineTabRef}
            role="tab"
            id={mineTabId}
            aria-selected={tab === "mine"}
            aria-controls={minePanelId}
            tabIndex={tab === "mine" ? 0 : -1}
            className={`feedback-tab${tab === "mine" ? " is-active" : ""}`}
            onClick={() => setTab("mine")}
            onKeyDown={onTabKeyDown}
          >
            My submissions
          </button>
        </div>
        <button type="button" className="feedback-x" onClick={onClose} aria-label="Close">×</button>
      </div>
      {tab === "send" ? (
        <div role="tabpanel" id={sendPanelId} aria-labelledby={sendTabId} tabIndex={0}>
          <FeedbackForm onClose={onClose} clearDraft={clearDraft} />
        </div>
      ) : (
        <div role="tabpanel" id={minePanelId} aria-labelledby={mineTabId} tabIndex={0}>
          <MySubmissions />
        </div>
      )}
    </>
  );
}

function FeedbackForm({
  onClose,
  clearDraft,
}: {
  onClose: () => void;
  clearDraft: RefObject<(() => void) | null>;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  // An UNCONTROLLED form — the message and the notify box are read off the DOM
  // by the action — so the draft takes the `formPort` adapter rather than
  // `field()`. `restore` because this is a create surface with no server-side
  // truth to contradict.
  const draft = useFormDraft({
    scope: "feedback:new",
    mode: "restore",
    // eslint-disable-next-line react-hooks/refs -- formPort reads ref.current only inside the snapshot/apply closures it returns, never during render; see formPort in useFormDraft.ts
    ...formPort(formRef, DRAFT_EXCLUDE),
  });
  const [state, formAction, pending] = useActionState(submitFeedback, INITIAL);
  const [type, setType] = useState("bug");
  const typeLabelId = useId();

  // Written during render on purpose, the same way `useFormDraft` holds its own
  // ports: the write is idempotent, so StrictMode's double render is harmless,
  // and an effect would leave a window in which closing the modal cleared
  // nothing. Deliberately never nulled — a close from the "My submissions" tab,
  // with this form unmounted, is still a close.
  // eslint-disable-next-line react-hooks/immutability -- publishing `clear` in an effect would leave a window in which closing the modal cleared nothing; the write is idempotent, so StrictMode's double render is harmless
  clearDraft.current = draft.clear;

  // Pulled out because the HANDLE is a fresh object every render while these
  // two are stable `useCallback`s. Depending on `draft` itself would re-run the
  // effect below on every render, and after a failed send that means re-writing
  // the flushed copy over whatever has been retyped since.
  const { clear: clearDraftNow, recoverAfterFailedSave } = draft;

  // Capture the page URL + user agent at submit time (client-side) rather than in
  // an effect — nothing window-dependent runs during render (no hydration risk),
  // and no set-state-in-effect.
  function action(fd: FormData) {
    fd.set("pageUrl", window.location.href);
    fd.set("userAgent", navigator.userAgent);
    return formAction(fd);
  }

  // LAYOUT effect, not effect: React resets the form during the MUTATION phase of
  // the commit that delivers this state, and a passive effect runs in a later
  // task — long enough to paint the blanked box before the words come back. A
  // layout effect lands in the same commit, straight after the reset.
  useLayoutEffect(() => {
    if (state.ok) {
      // Sent. Depends on the state OBJECT as well, not the boolean alone: `ok`
      // stays true across consecutive successes.
      clearDraftNow();
      const t = setTimeout(onClose, 1500);
      return () => clearTimeout(t);
    }
    // React 19 RESETS an uncontrolled form once its action completes — fine on
    // the success path above, where the form is replaced by the thanks line,
    // and ruinous here: the message blanks while the error asks the user to try
    // again. `onSubmit` flushed a copy before the attempt; this puts it back.
    if (state.error) recoverAfterFailedSave();
    // `state` is a fresh object per COMPLETED action, which is what makes this
    // run once per attempt rather than once per render.
  }, [state, clearDraftNow, recoverAfterFailedSave, onClose]);

  if (state.ok) return <div className="feedback-done">Thanks, your feedback is on its way.</div>;

  return (
    // The other half of `recoverAfterFailedSave`: React's post-action reset is
    // not ordered against the effect above, so a refusal already recovered
    // from refuses the reset rather than racing to refill after it.
    <form action={action} ref={formRef} onSubmit={() => draft.onSubmit()} onReset={draft.onReset} className="feedback-form">
      {/* `.feedback-form` carries no display of its own, so it is block flow and
          the always-mounted live region has no box here — no wrapper needed. */}
      <DraftNotice draft={draft.state} onRestore={draft.restore} onDiscard={draft.discard} />
      {/* A <span>, not a <label>: the control is a <button>, which a wrapping
          <label> cannot activate — it is named through aria-labelledby instead. */}
      <span className="feedback-field">
        <span id={typeLabelId}>Type</span>
        <SelectMenu
          name="type"
          className="selectbox"
          ariaLabelledBy={typeLabelId}
          placeholder="Choose a type"
          value={type}
          options={TYPE_OPTIONS}
          onChange={setType}
        />
      </span>

      <label className="feedback-field">
        <span>Message</span>
        <textarea name="message" rows={5} maxLength={5000} required
          className="feedback-textarea" placeholder="What's on your mind?" />
      </label>

      <label className="feedback-field">
        <span>Screenshot (optional)</span>
        <input type="file" name="screenshot" accept="image/*" className="feedback-file" />
      </label>

      <label className="feedback-check">
        <input type="checkbox" name="notify" />
        <span>Email me when this gets an update</span>
      </label>

      {state.error && <p className="feedback-error"><StatusDot tone="problem" />{state.error}</p>}

      <div className="feedback-actions">
        <button type="button" className="btn btn-quiet" onClick={onClose}>Cancel</button>
        <button type="submit" className="btn" disabled={pending}>{pending ? "Sending…" : "Send"}</button>
      </div>
    </form>
  );
}

function MySubmissions() {
  const [items, setItems] = useState<MyFeedbackItem[] | null>(null);
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    listMyFeedback().then((rows) => { if (alive) setItems(rows); }).catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  function toggle(id: string) {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  if (items === null) return <div className="feedback-done">Loading…</div>;
  if (items.length === 0) return <div className="feedback-done">You haven&apos;t sent any feedback yet.</div>;

  return (
    <ul className="feedback-list">
      {items.map((it) => {
        const isOpen = openIds.has(it.id);
        return (
          <li key={it.id} className="feedback-item">
            <button
              type="button"
              className="feedback-item-toggle"
              aria-expanded={isOpen}
              onClick={() => toggle(it.id)}
            >
              <span className="feedback-item-toggle-text">
                <span className="feedback-item-top">
                  <span className="feedback-item-type">{it.type}</span>
                  <span className={`feedback-badge status-${it.status}`}>
                    {STATUS_LABELS[it.status as FeedbackStatus] ?? it.status}
                  </span>
                </span>
                <span className="feedback-item-date">
                  {new Date(it.createdAt).toLocaleDateString()}
                  {it.updatedAt ? ` · updated ${new Date(it.updatedAt).toLocaleDateString()}` : ""}
                </span>
              </span>
              <Chevron open={isOpen} />
            </button>
            {isOpen && (
              <FeedbackThread
                // Formatted client-side: this list is fetched after mount, so
                // there is no SSR pass to disagree with — but through the same
                // pinned-zone `formatDateTime` the admin sees.
                head={{
                  body: it.message, createdAt: it.createdAt,
                  displayCreatedAt: formatDateTime(new Date(it.createdAt)), authorLabel: "You",
                }}
                messages={it.messages.map((m) => ({
                  ...m, emailStatus: null, displayCreatedAt: formatDateTime(new Date(m.createdAt)),
                }))}
                showEmailStatus={false}
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
